import { Product, Category, PriceOption } from "../data/catalog";
import { API } from "../config/constants";
import { fetchAdmin } from "./adminAuth";

const PUBLIC_API_URL = API.GET_PUBLIC_CAKES;
const ADMIN_API_URL = API.GET_CAKES;

interface ApiProduct {
  ID: string;
  Emoji: string;
  Nombre: string;
  Categoría: string;
  Descripción: string;
  "Porciones/Unidades": string;
  "Precio ($)": string;
  Badge: string;
  Imagen: string;
  Disponible?: string | boolean | number;
  Orden?: number;
}

export interface DeliveryZoneConfig {
  id: string;
  label: string;
  price: number;
  /** "fijo" (precio único) o "rango" (mínimo–máximo que elige el cliente) */
  priceMode?: "fijo" | "rango";
  priceMin?: number;
  priceMax?: number;
}

export interface DeliveryConfig {
  pickupAddress: string;
  zones: DeliveryZoneConfig[];
}

export const DEFAULT_DELIVERY_CONFIG: DeliveryConfig = {
  pickupAddress: "Torre Porteña",
  zones: [
    { id: "lecheria", label: "Lechería", price: 4, priceMode: "fijo" },
    { id: "barcelona", label: "Barcelona", price: 4, priceMode: "fijo" },
    { id: "puerto-la-cruz", label: "Puerto La Cruz", price: 3, priceMode: "fijo" },
    { id: "guanta", label: "Guanta", price: 2, priceMode: "rango", priceMin: 2, priceMax: 3 },
  ],
};

export const categories: Category[] = [
  {
    id: "roscas",
    name: "Roscas",
    emoji: "🥐",
    description: "Deliciosas roscas para compartir",
    color: "#fef3c7",
  },
  {
    id: "clasicas",
    name: "Tortas Clásicas",
    emoji: "🎂",
    description: "Los favoritos de siempre",
    color: "#ffe4e6",
  },
  {
    id: "frias",
    name: "Tortas Frías",
    emoji: "❄️",
    description: "Refrescantes y cremosas",
    color: "#e0f2fe",
  },
  {
    id: "choco",
    name: "ChocoLovers",
    emoji: "🍫",
    description: "Para los amantes del chocolate",
    color: "#fef3c7",
  },
  {
    id: "brownies",
    name: "Brownies & Brookies",
    emoji: "🍪",
    description: "Húmedos y crujientes",
    color: "#fef9c3",
  },
  {
    id: "postres",
    name: "Postres Fríos",
    emoji: "🥧",
    description: "Pies y gelatos artesanales",
    color: "#f0fdf4",
  },
  {
    id: "cheesecakes",
    name: "Cheesecakes",
    emoji: "🧀",
    description: "Suaves y cremosos",
    color: "#fdf4ff",
  },
  {
    id: "minis",
    name: "Mini Dulces",
    emoji: "🍬",
    description: "Perfectos para eventos",
    color: "#fff7ed",
  },
  {
    id: "edicion-especial",
    name: "Edición Especial",
    emoji: "✨",
    description: "Creaciones exclusivas de temporada",
    color: "#fdf2f8",
  },
  {
    id: "edicion-especial-2",
    name: "Edición Especial 2",
    emoji: "🌟",
    description: "Creaciones exclusivas de temporada (Campaña 2)",
    color: "#f0fdf4",
  },
];

// Transform API data to our Product format
const transformApiData = (apiData: ApiProduct[]): Product[] => {
  const productMap = new Map<string, Product>();

  apiData.forEach((item) => {
    if (item.ID.startsWith("config_")) return;
    const existingProduct = productMap.get(item.ID);

    const priceOption: PriceOption = {
      people: item["Porciones/Unidades"],
      price: parseInt(item["Precio ($)"].replace("$", "").replace(",", ".")),
    };

    const isAvailable = item.Disponible !== undefined && item.Disponible !== null
      ? item.Disponible.toString().toUpperCase() !== "FALSE"
      : true;

    if (existingProduct) {
      existingProduct.prices.push(priceOption);
      // Si alguno de los renglones está inactivo, desactivar
      if (!isAvailable) {
        existingProduct.available = false;
      }
    } else {
      const imageUrls = item.Imagen 
        ? item.Imagen.split(",").map(url => url.trim()).filter(Boolean)
        : [];

      const newProduct: Product = {
        id: item.ID,
        name: item.Nombre,
        description: item.Descripción,
        prices: [priceOption],
        category: (() => {
          const rawCategory = item.Categoría || "";
          const cleanRaw = rawCategory.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
          
          // Mapeo automático de Roscas por consistencia
          if (item.ID.startsWith("rosca-") || item.Nombre.toLowerCase().includes("rosca")) {
            return "roscas";
          }

          const matchedCategory = categories.find(cat => {
            const cleanName = cat.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
            const cleanId = cat.id.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
            return cleanRaw === cleanName || cleanRaw === cleanId;
          });
          return matchedCategory ? matchedCategory.id : cleanRaw;
        })(),
        emoji: item.Emoji,
        image: imageUrls.length > 0 ? imageUrls[0] : "",
        images: imageUrls,
        badge: item.Badge || undefined,
        available: isAvailable,
        orden: item.Orden !== undefined && item.Orden !== null ? Number(item.Orden) : undefined,
      };
      productMap.set(item.ID, newProduct);
    }
  });

  return Array.from(productMap.values());
};

type CatalogScope = "public" | "admin";

interface CatalogCache {
  data: ApiProduct[] | null;
  activePromise: Promise<ApiProduct[]> | null;
  timestamp: number;
  version: number;
}

const catalogCaches: Record<CatalogScope, CatalogCache> = {
  public: { data: null, activePromise: null, timestamp: 0, version: 0 },
  admin: { data: null, activePromise: null, timestamp: 0, version: 0 },
};

export const invalidatePublicCatalogCache = () => {
  const cache = catalogCaches.public;
  cache.version += 1;
  cache.data = null;
  cache.timestamp = 0;
  cache.activePromise = null;
};

const isCatalogRowAvailable = (row: Pick<ApiProduct, "Disponible">): boolean => {
  const value = row.Disponible;
  if (value === undefined || value === null) return true;
  if (value === false || value === 0) return false;
  const normalized = String(value).trim().toUpperCase();
  return normalized !== "FALSE" && normalized !== "0";
};

const isActiveCampaignRow = (row: ApiProduct): boolean => {
  if (!isCatalogRowAvailable(row)) return false;
  const available = String(row.Disponible ?? "").trim().toUpperCase();
  const state = String(row["Porciones/Unidades"] ?? "").trim().toLowerCase();
  return available === "TRUE" || state === "active" || state === "true";
};

const isPublicCatalogRow = (row: ApiProduct): boolean => {
  if (!row?.ID) return false;

  if (row.ID.startsWith("config_")) {
    if (row.ID === "config_campaign" || row.ID === "config_campaign_2") {
      return isActiveCampaignRow(row);
    }

    return (
      ["config_workshop", "config_custom_cakes", "config_delivery"].includes(row.ID) &&
      isCatalogRowAvailable(row)
    );
  }

  return isCatalogRowAvailable(row);
};

const getOfflineCatalogRows = (): ApiProduct[] => {
  try {
    const { localProducts } = require("../data/localProducts");
    if (!Array.isArray(localProducts)) return [];

    return localProducts.map((p: any) => ({
      ID: p.id,
      Emoji: p.emoji || "🎂",
      Nombre: p.name,
      Categoría: p.category,
      Descripción: p.description,
      "Porciones/Unidades": p.prices?.[0]?.people || "",
      "Precio ($)": `$${p.prices?.[0]?.price || 0}`,
      Badge: p.badge || "",
      Imagen: p.image || (p.images ? p.images.join(",") : ""),
      Disponible: p.available === false ? "FALSE" : "TRUE",
      Orden: p.orden,
    }));
  } catch (error) {
    console.error("Error al cargar el catálogo de respaldo offline:", error);
    return [];
  }
};

const fetchCatalogRows = async (
  scope: CatalogScope,
  forceRefresh = false,
): Promise<ApiProduct[]> => {
  const cache = catalogCaches[scope];
  const now = Date.now();

  if (cache.data && !forceRefresh && now - cache.timestamp < 15000) {
    return cache.data;
  }

  if (cache.activePromise) {
    return cache.activePromise;
  }

  const requestVersion = cache.version;

  cache.activePromise = (async () => {
    try {
      const endpoint = scope === "public" ? PUBLIC_API_URL : ADMIN_API_URL;
      const request = scope === "admin" ? fetchAdmin : fetch;
      const response = await request(`${endpoint}?t=${Date.now()}`).catch(() => null);
      const isJson =
        response &&
        response.ok &&
        response.headers.get("content-type")?.includes("application/json");

      if (isJson && response) {
        const parsed = await response.json();
        const data = Array.isArray(parsed) ? (parsed as ApiProduct[]) : [];
        const safeData = scope === "public" ? data.filter(isPublicCatalogRow) : data;
        if (cache.version === requestVersion) {
          cache.data = safeData;
          cache.timestamp = Date.now();
        }
        return safeData;
      }

      throw new Error("No se pudo obtener datos JSON válidos del servidor de base de datos.");
    } catch (error) {
      console.error("Error al obtener catálogo de MongoDB API:", error);
      const fallback = getOfflineCatalogRows();
      const safeFallback = scope === "public" ? fallback.filter(isPublicCatalogRow) : fallback;
      if (cache.version === requestVersion) {
        cache.data = safeFallback;
        cache.timestamp = Date.now();
      }
      return safeFallback;
    } finally {
      if (cache.version === requestVersion) {
        cache.activePromise = null;
      }
    }
  })();

  return cache.activePromise;
};

export const fetchRawCatalogData = (forceRefresh = false): Promise<ApiProduct[]> =>
  fetchCatalogRows("public", forceRefresh);

export const fetchAdminCatalogData = (forceRefresh = false): Promise<ApiProduct[]> => {
  invalidatePublicCatalogCache();
  return fetchCatalogRows("admin", forceRefresh);
};

export const fetchCatalogData = async (): Promise<Product[]> => {
  try {
    const rawData = await fetchRawCatalogData();
    return transformApiData(rawData);
  } catch (error) {
    console.error("Error in fetchCatalogData:", error);
    return [];
  }
};

/** Lee la configuración de delivery (zona + precio) de la fila config_delivery */
export const fetchDeliveryConfig = async (): Promise<DeliveryConfig> => {
  const defaults = {
    pickupAddress: DEFAULT_DELIVERY_CONFIG.pickupAddress,
    zones: DEFAULT_DELIVERY_CONFIG.zones.map((z) => ({ ...z })),
  };

  try {
    const rawData = await fetchRawCatalogData();
    const configRow = rawData.find((row) => row.ID === "config_delivery");
    if (!configRow) return defaults;

    let parsed: any = {};
    try {
      parsed = configRow["Porciones/Unidades"]
        ? JSON.parse(configRow["Porciones/Unidades"])
        : {};
    } catch {
      parsed = {};
    }

    const zones: DeliveryZoneConfig[] = Array.isArray(parsed.zones) && parsed.zones.length > 0
      ? parsed.zones
          .filter((z: any) => z && z.id && z.label !== undefined)
          .map((z: any) => {
            const zone: DeliveryZoneConfig = {
              id: String(z.id),
              label: String(z.label),
              price: Number(z.price) || 0,
              priceMode: z.priceMode === "rango" ? "rango" : "fijo",
            };
            if (z.priceMode === "rango") {
              zone.priceMin = Number(z.priceMin) || 0;
              zone.priceMax = Number(z.priceMax) || Number(z.price) || 0;
            }
            return zone;
          })
      : defaults.zones;

    return {
      pickupAddress: parsed.pickupAddress
        ? String(parsed.pickupAddress)
        : defaults.pickupAddress,
      zones,
    };
  } catch {
    return defaults;
  }
};
